// src/evaluations/AssessmentResultView.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Alert,
    Divider,
    Paper,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceArea,
    Cell
} from 'recharts';
import {
    getCompetencyScore,
    getAnsweredQuestionsCount,
    getTotalQuestionsCount
} from './competencyScoreUtils';

// Componente para las cards de leyenda
const LegendCard = styled(Card)(({ theme, color }) => ({
    border: `2px solid ${color}`,
    backgroundColor: `${color}15`,
    '&:hover': {
        backgroundColor: `${color}25`,
        transform: 'translateY(-1px)',
        boxShadow: theme.shadows[4],
    },
    transition: 'all 0.2s ease',
}));

const AssessmentResultView = ({ data }) => {
    const { t } = useTranslation();

    if (!data) return null;

    // Helper para obtener el color según el score
    const getScoreColor = (score) => {
        if (score >= 70) return "success";
        if (score >= 50) return "warning";
        return "error";
    };

    // Helper para convertir timestamp de Firestore a Date
    const fsTsToDate = (ts) => {
        if (!ts) return null;
        if (ts._seconds) return new Date(ts._seconds * 1000);
        if (ts.seconds) return new Date(ts.seconds * 1000);
        return new Date(ts);
    };

    // Helper para obtener label de NineBox
    const getNineBoxLabel = (overallScore, reportOverallScore) => {
        const x = Math.floor(overallScore / 34); // 0, 1, 2
        const y = Math.floor(reportOverallScore / 34); // 0, 1, 2

        const labels = [
            ['low-low', 'medium-low', 'high-low'],
            ['low-medium', 'medium-medium', 'high-medium'],
            ['low-high', 'medium-high', 'high-high']
        ];

        const position = labels[y]?.[x] || 'unclassified';
        return t(`evaluationResponses.nineBoxLabels.${position}`) || t('evaluationResponses.unclassified');
    };

    // Función para determinar el color según la zona (COLORES EXACTOS)
    const getZoneColor = (desempeño, perfil) => {
        // 1. Rendimiento bajo: perfil <=33 Y desempeño <=33
        if (perfil <= 33 && desempeño <= 33) return '#de3722';

        // 2. Rendimiento inconsistente: perfil <=33 Y desempeño >33 & <=66
        if (perfil <= 33 && desempeño > 33 && desempeño <= 66) return '#e3706a';

        // 3. Diamante en bruto: perfil <=33 Y desempeño >66
        if (perfil <= 33 && desempeño > 66) return '#f3da79';

        // 4. Profesional sólido: perfil >33 & <=66 Y desempeño <=33
        if (perfil > 33 && perfil <= 66 && desempeño <= 33) return '#e3706a';

        // 5. Profesional clave de alto rendimiento: perfil >33 & <=66 Y desempeño >33 & <=66
        if (perfil > 33 && perfil <= 66 && desempeño > 33 && desempeño <= 66) return '#eec245';

        // 6. Futura estrella: perfil >33 & <=66 Y desempeño >66
        if (perfil > 33 && perfil <= 66 && desempeño > 66) return '#b1ce94';

        // 7. Profesional altamente calificado: perfil >66 Y desempeño <=33
        if (perfil > 66 && desempeño <= 33) return '#f3da79';

        // 8. Profesional más que altamente calificado: perfil >66 Y desempeño >33 & <=66
        if (perfil > 66 && desempeño > 33 && desempeño <= 66) return '#b1ce94';

        // 9. Estrella consistente: perfil >66 Y desempeño >66
        if (perfil > 66 && desempeño > 66) return '#80ab56';

        return '#cccccc'; // fallback
    };

    // Función para obtener la clasificación en texto (USANDO LAS CLAVES CORRECTAS)
    const getClassification = (desempeño, perfil) => {
        // 1. Rendimiento bajo: perfil <=33 Y desempeño <=33
        if (perfil <= 33 && desempeño <= 33) return t('evaluationResponses.talentMap.underPerformer');

        // 2. Rendimiento inconsistente: perfil <=33 Y desempeño >33 & <=66
        if (perfil <= 33 && desempeño > 33 && desempeño <= 66) return t('evaluationResponses.talentMap.developmentNeeded');

        // 3. Diamante en bruto: perfil <=33 Y desempeño >66
        if (perfil <= 33 && desempeño > 66) return t('evaluationResponses.talentMap.inconsistentPerformer');

        // 4. Profesional sólido: perfil >33 & <=66 Y desempeño <=33
        if (perfil > 33 && perfil <= 66 && desempeño <= 33) return t('evaluationResponses.talentMap.coreProfessional');

        // 5. Profesional clave de alto rendimiento: perfil >33 & <=66 Y desempeño >33 & <=66
        if (perfil > 33 && perfil <= 66 && desempeño > 33 && desempeño <= 66) return t('evaluationResponses.talentMap.keyContributor');

        // 6. Futura estrella: perfil >33 & <=66 Y desempeño >66
        if (perfil > 33 && perfil <= 66 && desempeño > 66) return t('evaluationResponses.talentMap.emergingTalent');

        // 7. Profesional altamente calificado: perfil >66 Y desempeño <=33
        if (perfil > 66 && desempeño <= 33) return t('evaluationResponses.talentMap.highPotential');

        // 8. Profesional más que altamente calificado: perfil >66 Y desempeño >33 & <=66
        if (perfil > 66 && desempeño > 33 && desempeño <= 66) return t('evaluationResponses.talentMap.highPerformer');

        // 9. Estrella consistente: perfil >66 Y desempeño >66
        if (perfil > 66 && desempeño > 66) return t('evaluationResponses.talentMap.starPerformer');

        return t('evaluationResponses.talentMap.unclassified');
    };

    // Custom Tooltip para el gráfico
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <Paper sx={{ p: 2, border: '1px solid #ccc' }}>
                    <Typography variant="body2" fontWeight="bold">
                        {data.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('evaluationResponses.talentMap.performance')}: {data.desempeño}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('evaluationResponses.talentMap.profile')}: {data.perfil}%
                    </Typography>
                    <Chip
                        label={getClassification(data.desempeño, data.perfil)}
                        size="small"
                        sx={{
                            mt: 1,
                            backgroundColor: getZoneColor(data.desempeño, data.perfil) + '20',
                            color: getZoneColor(data.desempeño, data.perfil),
                            fontWeight: 'bold'
                        }}
                    />
                </Paper>
            );
        }
        return null;
    };

    const overallScore = data.overallScore || 0;
    const reportOverallScore = data.reportOverallScore || 0;
    const confidenceIndex = data.confidenceIndex || 0;

    const plan = data.plan_desarrollo || {};

    const plan90 = plan.dias_90 || plan.primeros_90_dias;
    const plan180 = plan.dias_180 || plan.siguientes_90_dias;
    const plan360 = plan.dias_360 || plan.tercer_periodo;

    // Datos para el scatter chart (un solo punto)
    const chartData = [{
        name: data.applicantName || 'Candidato',
        desempeño: overallScore,
        perfil: reportOverallScore
    }];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header Card */}
            <Card variant="outlined" sx={{ mb: 3, bgcolor: '#f8fafc' }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t('evaluationResponses.candidate')}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {data.applicantName || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t('evaluationResponses.position')}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {data.positionName || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t('evaluationResponses.completedDate')}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {data.completedAt
                                    ? fsTsToDate(data.completedAt)?.toLocaleString('es-ES')
                                    : 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t('evaluationResponses.confidenceIndex')}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {confidenceIndex}%
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Scores principales */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('evaluationResponses.scoreAssessment')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h2" fontWeight={700} color={`${getScoreColor(overallScore)}.main`}>
                                    {overallScore}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    / 100
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={overallScore}
                                color={getScoreColor(overallScore)}
                                sx={{ height: 10, borderRadius: 1 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('evaluationResponses.scorePsycholabor')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h2" fontWeight={700} color={`${getScoreColor(reportOverallScore)}.main`}>
                                    {reportOverallScore}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    / 100
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={reportOverallScore}
                                color={getScoreColor(reportOverallScore)}
                                sx={{ height: 10, borderRadius: 1 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Competencias evaluadas con fortalezas y áreas de mejora */}
            {data.capabilities && data.capabilities.length > 0 && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon />
                            {t('evaluationResponses.evaluatedCompetencies')}
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        {data.capabilities.map((capability, capIdx) => {
                            const competencyScore = getCompetencyScore(data, capability);
                            const answeredCount = getAnsweredQuestionsCount(capability);
                            const totalCount = getTotalQuestionsCount(capability);

                            // Extraer fortalezas y áreas de mejora de esta competencia
                            const strengths = [];
                            const improvements = [];

                            capability.questions?.forEach(q => {
                                if (q.strengthsForPosition) strengths.push(...q.strengthsForPosition);
                                if (q.improvementAreas) improvements.push(...q.improvementAreas);
                            });

                            const uniqueStrengths = [...new Set(strengths)];
                            const uniqueImprovements = [...new Set(improvements)];

                            return (
                                <Box key={capIdx} sx={{ mb: 4, '&:last-child': { mb: 0 } }}>
                                    {/* Header de la competencia */}
                                    <Box sx={{
                                        p: 2,
                                        bgcolor: '#f8fafc',
                                        borderRadius: 1,
                                        mb: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Typography variant="h6" fontWeight={600}>
                                            {capability.capabilityName}
                                        </Typography>
                                        <Chip
                                            label={`${competencyScore}/100`}
                                            color={getScoreColor(competencyScore)}
                                            size="medium"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        color={answeredCount < totalCount ? 'warning.main' : 'text.secondary'}
                                        sx={{ mb: 2 }}
                                    >
                                        {t('evaluationResponses.questionsAnswered', {
                                            answered: answeredCount,
                                            total: totalCount
                                        })}
                                    </Typography>

                                    {/* Fortalezas y Áreas de Mejora de esta competencia */}
                                    <Grid container spacing={2}>
                                        {uniqueStrengths.length > 0 && (
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" fontWeight={600} color="success.main" gutterBottom>
                                                    {t('evaluationResponses.strengthsForPosition')}
                                                </Typography>
                                                <List dense>
                                                    {uniqueStrengths.map((strength, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon color="success" fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={strength}
                                                                primaryTypographyProps={{
                                                                    variant: "body2",
                                                                    color: "text.secondary"
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Grid>
                                        )}

                                        {uniqueImprovements.length > 0 && (
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" fontWeight={600} color="warning.main" gutterBottom>
                                                    {t('evaluationResponses.improvementAreas')}
                                                </Typography>
                                                <List dense>
                                                    {uniqueImprovements.map((improvement, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <WarningIcon color="warning" fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={improvement}
                                                                primaryTypographyProps={{
                                                                    variant: "body2",
                                                                    color: "text.secondary"
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Grid>
                                        )}
                                    </Grid>

                                    {capIdx < data.capabilities.length - 1 && <Divider sx={{ mt: 3 }} />}
                                </Box>
                            );
                        })}
                    </CardContent>
                </Card>
            )}

            {/* Posición en Matriz NineBox CON GRÁFICO */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('evaluationResponses.tabTalentMap')}
                    </Typography>

                    <Grid container spacing={3}>
                        {/* Gráfico principal */}
                        <Grid item xs={12} lg={8}>
                            <Box sx={{ height: 500, width: '100%' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                                        {/* Cuadrantes de fondo - COLORES EXACTOS */}

                                        {/* FILA SUPERIOR (Perfil Alto >66) */}
                                        <ReferenceArea x1={0} x2={33} y1={66} y2={100} fill="#f3da79" fillOpacity={0.3} />
                                        <ReferenceArea x1={33} x2={66} y1={66} y2={100} fill="#b1ce94" fillOpacity={0.3} />
                                        <ReferenceArea x1={66} x2={100} y1={66} y2={100} fill="#80ab56" fillOpacity={0.3} />

                                        {/* FILA MEDIA (Perfil Medio >33 & <=66) */}
                                        <ReferenceArea x1={0} x2={33} y1={33} y2={66} fill="#e3706a" fillOpacity={0.3} />
                                        <ReferenceArea x1={33} x2={66} y1={33} y2={66} fill="#eec245" fillOpacity={0.3} />
                                        <ReferenceArea x1={66} x2={100} y1={33} y2={66} fill="#b1ce94" fillOpacity={0.3} />

                                        {/* FILA INFERIOR (Perfil Bajo <=33) */}
                                        <ReferenceArea x1={0} x2={33} y1={0} y2={33} fill="#de3722" fillOpacity={0.3} />
                                        <ReferenceArea x1={33} x2={66} y1={0} y2={33} fill="#e3706a" fillOpacity={0.3} />
                                        <ReferenceArea x1={66} x2={100} y1={0} y2={33} fill="#f3da79" fillOpacity={0.3} />

                                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

                                        <XAxis
                                            type="number"
                                            dataKey="desempeño"
                                            name={t('evaluationResponses.talentMap.performance')}
                                            domain={[0, 100]}
                                            label={{
                                                value: t('evaluationResponses.talentMap.performanceAxis'),
                                                position: 'insideBottom',
                                                offset: -10,
                                                style: { fontSize: 14, fontWeight: 'bold' }
                                            }}
                                            tick={{ fontSize: 12 }}
                                        />

                                        <YAxis
                                            type="number"
                                            dataKey="perfil"
                                            name={t('evaluationResponses.talentMap.profile')}
                                            domain={[0, 100]}
                                            label={{
                                                value: t('evaluationResponses.talentMap.profileAxis'),
                                                angle: -90,
                                                position: 'insideLeft',
                                                style: { fontSize: 14, fontWeight: 'bold' }
                                            }}
                                            tick={{ fontSize: 12 }}
                                        />

                                        <Tooltip content={<CustomTooltip />} />

                                        <Scatter name={data.applicantName} data={chartData} fill="#8884d8">
                                            {chartData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={getZoneColor(entry.desempeño, entry.perfil)}
                                                    stroke="#fff"
                                                    strokeWidth={3}
                                                    r={12}
                                                />
                                            ))}
                                        </Scatter>
                                    </ScatterChart>
                                </ResponsiveContainer>
                            </Box>
                        </Grid>

                        {/* Leyenda con LAS 9 CLASIFICACIONES */}
                        <Grid item xs={12} lg={4}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    {t('evaluationResponses.talentMap.legend')}
                                </Typography>

                                {/* Zona 1: Estrella Consistente */}
                                <LegendCard color="#80ab56">
                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#4A6741', fontSize: '0.75rem' }}>
                                            1. {t('evaluationResponses.talentMap.starPerformer')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {t('evaluationResponses.talentMap.starPerformerDesc')}
                                        </Typography>
                                    </CardContent>
                                </LegendCard>

                                {/* Zona 2: Profesional más que Altamente Calificado */}
                                <LegendCard color="#b1ce94">
                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#6B8E23', fontSize: '0.75rem' }}>
                                            2. {t('evaluationResponses.talentMap.highPerformer')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {t('evaluationResponses.talentMap.highPerformerDesc')}
                                        </Typography>
                                    </CardContent>
                                </LegendCard>

                                {/* Zona 3: Profesional Altamente Calificado */}
                                <LegendCard color="#f3da79">
                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#8B6914', fontSize: '0.75rem' }}>
                                            3. {t('evaluationResponses.talentMap.highPotential')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {t('evaluationResponses.talentMap.highPotentialDesc')}
                                        </Typography>
                                    </CardContent>
                                </LegendCard>

                                {/* Zona 4: Futura Estrella */}
                                <LegendCard color="#b1ce94">
                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#6B8E23', fontSize: '0.75rem' }}>
                                            4. {t('evaluationResponses.talentMap.emergingTalent')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {t('evaluationResponses.talentMap.emergingTalentDesc')}
                                        </Typography>
                                    </CardContent>
                                </LegendCard>

                                {/* Zona 5: Profesional Clave de Alto Rendimiento */}
                                <LegendCard color="#eec245">
                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#8B6914', fontSize: '0.75rem' }}>
                                            5. {t('evaluationResponses.talentMap.keyContributor')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {t('evaluationResponses.talentMap.keyContributorDesc')}
                                        </Typography>
                                    </CardContent>
                                </LegendCard>

                                {/* Zona 6: Profesional Sólido */}
                                <LegendCard color="#e3706a">
                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#8B0000', fontSize: '0.75rem' }}>
                                            6. {t('evaluationResponses.talentMap.coreProfessional')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {t('evaluationResponses.talentMap.coreProfessionalDesc')}
                                        </Typography>
                                    </CardContent>
                                </LegendCard>

                                {/* Zona 7: Un Diamante en Bruto */}
                                <LegendCard color="#f3da79">
                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#8B6914', fontSize: '0.75rem' }}>
                                            7. {t('evaluationResponses.talentMap.inconsistentPerformer')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {t('evaluationResponses.talentMap.inconsistentPerformerDesc')}
                                        </Typography>
                                    </CardContent>
                                </LegendCard>

                                {/* Zona 8: Trabajador de Rendimiento Inconsistente */}
                                <LegendCard color="#e3706a">
                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#8B0000', fontSize: '0.75rem' }}>
                                            8. {t('evaluationResponses.talentMap.developmentNeeded')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {t('evaluationResponses.talentMap.developmentNeededDesc')}
                                        </Typography>
                                    </CardContent>
                                </LegendCard>

                                {/* Zona 9: Trabajador de Rendimiento Bajo */}
                                <LegendCard color="#de3722">
                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#8B0000', fontSize: '0.75rem' }}>
                                            9. {t('evaluationResponses.talentMap.underPerformer')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {t('evaluationResponses.talentMap.underPerformerDesc')}
                                        </Typography>
                                    </CardContent>
                                </LegendCard>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Alert con información del candidato */}
                    <Alert severity="info" sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                            {getClassification(overallScore, reportOverallScore)}
                        </Typography>
                        <Typography variant="body2">
                            {t('evaluationResponses.xAxisAssessment')} {overallScore} | {t('evaluationResponses.yAxisJobFit')} {reportOverallScore}
                        </Typography>
                    </Alert>

                    {/* Análisis NineBox */}
                    {data.analisis_ninebox && (
                        <Box sx={{ mt: 3 }}>
                            {typeof data.analisis_ninebox === 'string' || data.analisis_ninebox.analisis ? (
                                // Formato antiguo
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
                                    {data.analisis_ninebox.analisis || data.analisis_ninebox}
                                </Typography>
                            ) : (
                                // Formato nuevo (objeto estructurado)
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {data.analisis_ninebox.significado_cuadrante && (
                                        <Box>
                                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                                {t('evaluationResponses.quadrantMeaning')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                {data.analisis_ninebox.significado_cuadrante}
                                            </Typography>
                                        </Box>
                                    )}

                                    {data.analisis_ninebox.razones_posicionamiento && (
                                        <Box>
                                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                                {t('evaluationResponses.positioningReasons')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                {data.analisis_ninebox.razones_posicionamiento}
                                            </Typography>
                                        </Box>
                                    )}

                                    {data.analisis_ninebox.elementos_oportunidades && (
                                        <Box>
                                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                                {t('evaluationResponses.elementsAndOpportunities')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                {data.analisis_ninebox.elementos_oportunidades}
                                            </Typography>
                                        </Box>
                                    )}

                                    {data.analisis_ninebox.expectativas && (
                                        <Box>
                                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                                {t('evaluationResponses.expectations6to12')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                {data.analisis_ninebox.expectativas}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Plan de Desarrollo */}
            {data.plan_desarrollo && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {t('evaluationResponses.developmentPlan90180360')}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {plan.introduccion && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                                {plan.introduccion}
                            </Typography>
                        )}

                        <Grid container spacing={2}>
                            {plan90 && (
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
                                        <Typography variant="subtitle1" fontWeight={600} color="primary.main" gutterBottom>
                                            {t('evaluationResponses.first90Days')}
                                        </Typography>

                                        {plan90.objetivos?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.objectives')}
                                                </Typography>
                                                <List dense>
                                                    {plan90.objetivos.map((obj, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <TrendingUpIcon fontSize="small" color="primary" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={obj} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {plan90.acciones?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.actions')}
                                                </Typography>
                                                <List dense>
                                                    {plan90.acciones.map((acc, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={acc} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {plan90.indicadores?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.indicators')}
                                                </Typography>
                                                <List dense>
                                                    {plan90.indicadores.map((ind, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={ind} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                            )}

                            {plan180 && (
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
                                        <Typography variant="subtitle1" fontWeight={600} color="secondary.main" gutterBottom>
                                            {t('evaluationResponses.next90Days')}
                                        </Typography>

                                        {plan180.objetivos?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.objectives')}
                                                </Typography>
                                                <List dense>
                                                    {plan180.objetivos.map((obj, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <TrendingUpIcon fontSize="small" color="secondary" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={obj} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {plan180.acciones?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.actions')}
                                                </Typography>
                                                <List dense>
                                                    {plan180.acciones.map((acc, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={acc} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {plan180.indicadores?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.indicators')}
                                                </Typography>
                                                <List dense>
                                                    {plan180.indicadores.map((ind, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={ind} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                            )}

                            {plan360 && (
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
                                        <Typography variant="subtitle1" fontWeight={600} color="success.main" gutterBottom>
                                            {t('evaluationResponses.thirdPeriod')}
                                        </Typography>

                                        {plan360.objetivos?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.objectives')}
                                                </Typography>
                                                <List dense>
                                                    {plan360.objetivos.map((obj, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <TrendingUpIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={obj} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {plan360.acciones?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.actions')}
                                                </Typography>
                                                <List dense>
                                                    {plan360.acciones.map((acc, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={acc} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {plan360.indicadores?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.indicators')}
                                                </Typography>
                                                <List dense>
                                                    {plan360.indicadores.map((ind, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={ind} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Conclusión Integrada */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('evaluationResponses.integratedConclusion')}
                    </Typography>

                    {data.conclusion_integrada ? (
                        typeof data.conclusion_integrada === 'string' ? (
                            // Formato antiguo (string con párrafos)
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
                                {data.conclusion_integrada}
                            </Typography>
                        ) : (
                            // Formato nuevo (objeto estructurado)
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {data.conclusion_integrada.vision_general && (
                                    <Box>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {t('evaluationResponses.generalPerformanceView')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                            {data.conclusion_integrada.vision_general}
                                        </Typography>
                                    </Box>
                                )}

                                {data.conclusion_integrada.conexion_disc && (
                                    <Box>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {t('evaluationResponses.discConnection')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                            {data.conclusion_integrada.conexion_disc}
                                        </Typography>
                                    </Box>
                                )}

                                {data.conclusion_integrada.capacidades_cognitivas && (
                                    <Box>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {t('evaluationResponses.cognitiveCapabilities')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                            {data.conclusion_integrada.capacidades_cognitivas}
                                        </Typography>
                                    </Box>
                                )}

                                {data.conclusion_integrada.oportunidades_desarrollo && (
                                    <Box>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {t('evaluationResponses.developmentOpportunities')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                            {data.conclusion_integrada.oportunidades_desarrollo}
                                        </Typography>
                                    </Box>
                                )}

                                {data.conclusion_integrada.sintesis_perfil && (
                                    <Box>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {t('evaluationResponses.profileSynthesis')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                            {data.conclusion_integrada.sintesis_perfil}
                                        </Typography>
                                    </Box>
                                )}

                                {data.conclusion_integrada.implicaciones_cargo && (
                                    <Box>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {t('evaluationResponses.positionImplications')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                            {data.conclusion_integrada.implicaciones_cargo}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {t('evaluationResponses.noConclusionAvailable')}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default AssessmentResultView;